import { useSyncExternalStore } from "react";

/**
 * Lichte, afhankelijkheidsvrije i18n voor de UI-teksten. Vertaalt op basis van de
 * Nederlandse brontekst: `t("Bekijk product")`. Ontbrekende vertalingen vallen netjes
 * terug op het Nederlands, zodat een (deels) gevulde EN-dictionary altijd werkt.
 * De productcatalogus (namen/omschrijvingen van Timmers) blijft Nederlands.
 *
 * Voorkeur in localStorage (instant + werkt voor gasten); zet ook <html lang>.
 */
export type Locale = "nl" | "en";

const KEY = "glastotaal.locale";

function read(): Locale {
  // 1. De URL wint boven alles. Sinds de Engelse pagina's een eigen /en-pad hebben, IS het pad de
  //    taal: wie op /en/... binnenkomt (uit Google, of via een gedeelde link) hoort Engels te zien,
  //    ongeacht wat er ooit in deze browser is opgeslagen. Zonder deze regel zou een bezoeker met
  //    "nl" in localStorage een Engelse URL in het Nederlands te zien krijgen.
  try {
    if (location.pathname === "/en" || location.pathname.startsWith("/en/")) return "en";
  } catch {
    /* geen location (prerender/Node) */
  }
  try {
    const v = localStorage.getItem(KEY);
    if (v === "nl" || v === "en") return v; // expliciete keuze van de bezoeker wint daarna
  } catch {
    /* geen storage */
  }
  // 3. Nog geen keuze → Nederlands. GEEN automatische omschakeling op browsertaal meer: sinds de
  //    Engelse pagina's een eigen /en-pad hebben, IS de URL de taal. Googlebot crawlt met een
  //    en-US-browser, dus zette die detectie de Nederlandse URL's ná hydratatie in het Engels —
  //    met Engelse titels ("Castolin torch … incl. VAT") in de Nederlandse zoekresultaten tot
  //    gevolg. Engelstalige bezoekers komen via hreflang op /en/... uit, of via de taalknop.
  return "nl";
}

function applyLang(l: Locale): void {
  try {
    document.documentElement.lang = l;
  } catch {
    /* geen document (SSR/prerender) */
  }
}

let current: Locale = read();
applyLang(current);
const listeners = new Set<() => void>();

/**
 * Catalogus-namen (accessoire-/glasartikelen, ~3.3k stuks) worden NIET eager in de UI-bundle
 * gemergd (te zwaar voor de NL-standaardervaring), maar LAZY geladen zodra de taal Engels is.
 * Tot ze binnen zijn valt `translate()` netjes terug op het Nederlands; na het laden triggeren
 * we een re-render zodat namen alsnog Engels worden.
 */
let catalogNames: Record<string, string> = {};
let catalogRequested = false;
/**
 * De UI-dictionary (i18n-en/*.ts, ~420 kB bron) gaat op dezelfde manier: lazy, alleen wanneer de
 * taal Engels is. Die stond hier eerst als statische import — dan downloadt élke Nederlandse
 * bezoeker het volledige Engelse woordenboek in de hoofdbundel, voor een taal die hij nooit
 * aanzet. Zelfde terugval als de catalogusnamen: tot de dictionary binnen is, is de tekst even
 * Nederlands, daarna rendert alles opnieuw.
 */
let EN: Record<string, string> = {};
let enRequested = false;
function loadEnDict(): void {
  if (enRequested) return;
  enRequested = true;
  import("./i18n-en")
    .then((m) => {
      EN = m.EN;
      catalogVersion++; // zelfde her-render-mechaniek als de catalogusnamen
      listeners.forEach((fn) => fn());
    })
    .catch(() => {
      /* val terug op NL */
    });
}
/** Bumpt zodra de lazy namen-map binnen is; hangt aan dezelfde listeners als de locale-store,
 *  zodat memo's die van catalogusnamen afhangen (bv. de zoek-index) opnieuw berekenen. */
let catalogVersion = 0;
function loadCatalogNames(): void {
  if (catalogRequested) return;
  catalogRequested = true;
  import("../../data/seed/catalog-names.en.json")
    .then((m) => {
      catalogNames = (m.default ?? m) as Record<string, string>;
      catalogVersion++;
      listeners.forEach((fn) => fn());
    })
    .catch(() => {
      /* val terug op NL-namen */
    });
}
// Automatische detectie (browsertaal, geen expliciete keuze) mag niet met de eerste render
// concurreren — zelfde afweging als firebaseIdle() elders. Een Lighthouse/PageSpeed-run start
// standaard met browsertaal en-US, dus zónder dit wachtte ELKE meting op deze site altijd de
// volledige Engelse dictionary + catalogusnamen in de kritieke laadfase — iets wat een echte
// (grotendeels Nederlandstalige) bezoeker vrijwel nooit tegenkomt. Een EXPLICIETE taalkeuze
// (setLocale hieronder, de gebruiker klikt zelf) blijft wél direct laden.
if (current === "en" && typeof window !== "undefined") {
  const run = (): void => { loadCatalogNames(); loadEnDict(); };
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (ric) ric(run, { timeout: 2500 });
  else setTimeout(run, 1200);
}

/** Taal zetten (en sitebreed laten doorwerken). */
export function setLocale(l: Locale): void {
  if (l === current) return;
  current = l;
  if (l === "en") { loadCatalogNames(); loadEnDict(); }
  try {
    localStorage.setItem(KEY, l);
  } catch {
    /* geen storage */
  }
  applyLang(l);
  listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Reactieve taal. Tuple-API: [locale, setLocale]. */
export function useLocale(): [Locale, (l: Locale) => void] {
  const loc = useSyncExternalStore(subscribe, () => current, () => current);
  return [loc, setLocale];
}

/** De actieve taal buiten React (bv. voor PDF-/documentgeneratie in data-modules). */
export function getLocale(): Locale {
  return current;
}

/**
 * Tijdsduren uit de ADMIN ("1-3 weken", "2-3 werkdagen", "13-15 weken") staan niet in de dictionary:
 * Thijs kan ze in Instellingen wijzigen, dus een vaste sleutel veroudert meteen. Op de Engelse site
 * stond daardoor "delivery time 1-3 weken" (gezien 20-09-2026). Deze regel vertaalt alleen de
 * eenheid en laat de getallen staan, dus elke nieuwe waarde loopt vanzelf mee.
 */
const DUUR_EENHEID: Record<string, string> = {
  werkdagen: "working days", werkdag: "working day",
  weken: "weeks", week: "week",
  dagen: "days", dag: "day",
  maanden: "months", maand: "month",
  uur: "hours",
};
const DUUR = new RegExp(`^(\\d+(?:\\s*[-–—/]\\s*\\d+)?)\\s*(${Object.keys(DUUR_EENHEID).join("|")})$`, "i");

function vertaalDuur(nl: string): string | null {
  const m = DUUR.exec(nl.trim());
  if (!m) return null;
  const eenheid = DUUR_EENHEID[m[2]!.toLowerCase()];
  return eenheid ? `${m[1]!.replace(/\s+/g, "")} ${eenheid}` : null;
}

/** Vertaal een Nederlandse brontekst naar de actieve (of meegegeven) taal. */
export function translate(nl: string, locale: Locale = current): string {
  if (locale === "en") return EN[nl] ?? catalogNames[nl] ?? vertaalDuur(nl) ?? nl;
  return nl;
}

/**
 * Vertaalt een NL-template en vult daarna `{token}`-plaatshouders in. De dictionary-sleutel is
 * de NL-template MÉT tokens (bv. `"Max. {max} mm"`), zodat dynamische meldingen (validatie-/
 * foutteksten met getallen) tóch vertaalbaar zijn — anders zou de gebakken tekst met getallen
 * nooit matchen. Bruikbaar buiten React (leest de module-brede `current`-taal).
 */
export function translateFormat(nl: string, params?: Record<string, string | number>, locale: Locale = current): string {
  let s = translate(nl, locale);
  if (params) for (const [k, v] of Object.entries(params)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Reactieve versie van de lazy catalogusnamen-map (voor memo-deps). Verandert zodra de map
 *  geladen is, zodat afgeleide berekeningen (zoek-index e.d.) opnieuw draaien met EN-namen. */
export function useCatalogVersion(): number {
  return useSyncExternalStore(subscribe, () => catalogVersion, () => catalogVersion);
}

/** Hook: geeft een reactieve `t(nl)` terug die meeverandert met de taal ÉN met het binnenkomen
 *  van de lazy catalogusnamen-map. Dat laatste is cruciaal: catalogusnamen (artikelen/categorieën)
 *  worden ná de eerste render async geladen; zonder `catalogVersion` in de snapshot zou
 *  useSyncExternalStore niet opnieuw renderen (de taal is immers ongewijzigd) en bleven die namen
 *  in het Nederlands hangen. */
export function useT(): (nl: string) => string {
  const key = useSyncExternalStore(subscribe, () => `${current}:${catalogVersion}`, () => `${current}:${catalogVersion}`);
  void key;
  return (nl: string) => translate(nl, current);
}
